// Global Instructions Rule Applied!
// Shape of GET /fund-transactions/fund/:fundId/transfer-preview
// (view__fund_transfer_preview): a commitment's position as at a transfer
// date - called / paid / outstanding / uncalled plus the credit-positive
// capital-account components, the balance a whole transfer hands over and
// the transferable (stamped) balance a partial transfer hands over pro-rata.
// The same object is stamped on the transfer at Post
// (detail.transfer.position_at_transfer).
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

// split transfers in / out of the commitment through the date (A3.7)
export const FundTransferPreviewTransferBucketSchema = z
  .object({
    contributions: z.number(),
    distributions: z.number(),
    management_fees: z.number(),
    other_expenses: z.number(),
    income_and_gains: z.number(),
    balance: z.number(),
    fees_called: z.number(),
    fees_expensed: z.number(),
    called: z.number(),
    paid: z.number(),
    amount: z.number()
  })
  .strict()

export const FundTransferPreviewCapitalSchema = z
  .object({
    contributions: z.number(),
    distributions: z.number(),
    management_fees: z.number(),
    other_expenses: z.number(),
    income_and_gains: z.number(),
    transfers: z.number(),
    balance: z.number(),
    // the stamped part of the balance: what a split hands over per unit of fraction
    stamped_balance: z.number(),
    fees_called: z.number(),
    fees_expensed: z.number(),
    share: z.number(),
    transfers_in: FundTransferPreviewTransferBucketSchema,
    transfers_out: FundTransferPreviewTransferBucketSchema
  })
  .strict()

export const FundTransferPreviewSchema = z
  .object({
    fund_commitments__id: z.string(),
    investors__name: z.string().nullish(),
    cfg_fund_commitment_statuses__label: z.string().nullish(),
    fund_commitments__amount: z.number().nullish(),
    fund_commitments__amount_fmt: z.string().nullish(),
    fund_commitments__date_closed: z.string().nullish(),
    prog__as_of: z.string(),
    prog__called: z.number(),
    prog__called_fmt: z.string().nullish(),
    prog__paid: z.number(),
    prog__paid_fmt: z.string().nullish(),
    prog__outstanding: z.number(),
    prog__outstanding_fmt: z.string().nullish(),
    prog__uncalled: z.number(),
    prog__uncalled_fmt: z.string().nullish(),
    capital: FundTransferPreviewCapitalSchema,
    prog__capital_balance: z.number(),
    prog__capital_balance_fmt: z.string().nullish(),
    prog__transferable_balance: z.number(),
    prog__transferable_balance_fmt: z.string().nullish(),
    prog__contributions_fmt: z.string().nullish(),
    prog__distributions_fmt: z.string().nullish(),
    prog__management_fees_fmt: z.string().nullish(),
    prog__other_expenses_fmt: z.string().nullish(),
    prog__income_and_gains_fmt: z.string().nullish()
  })
  .strict()

export type FundTransferPreview = z.infer<typeof FundTransferPreviewSchema>

export const validateFundTransferPreview = (data: unknown): FundTransferPreview =>
  validateWithZodSchema(FundTransferPreviewSchema, data, 'fund transfer preview')
